import { useMemo, useEffect } from 'react';
import { useParams } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { PractitionerProfile, BeltPromotion, GradeInfo, ProfileCompany } from 'app/javascript/types';
import ProfileView from 'app/javascript/components/profile/ProfileView';
import { Belt, Grade } from 'app/javascript/services/belts';
import { trackEvent } from 'app/javascript/firebase';

const PUBLIC_PROFILES_PATH = '/api/public/profiles';

const convertGradeToInfo = (grade: Grade): GradeInfo => ({
  id: grade.id.toString(),
  number: grade.number,
  date: grade.received_at,
  instructorName: undefined,
});

const convertBeltToPromotion = (belt: Belt): BeltPromotion => ({
  id: belt.id.toString(),
  rank: belt.rank,
  date: belt.belt_received_at || '',
  instructorName: '',
  isValidated: belt.confirmed,
  notes: undefined,
  grades: belt.grades?.map(convertGradeToInfo) || [],
  checkInPhotos: (belt.check_in_photos || []).map((photo) => ({
    id: photo.id.toString(),
    classDate: photo.class_date,
    photoUrl: photo.photo_url,
    thumbnailUrl: photo.thumbnail_url,
  })),
});

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function mapApiToProfile(userData: any): PractitionerProfile {
  const bjjProfile = userData.bjj_profile;
  const belts = bjjProfile?.belts || [];

  const companies: ProfileCompany[] = (userData.academies || []).map((a: { id: number; name: string; slug: string; logo_url?: string; role?: string; address?: string; city?: string; state?: string; phone?: string; website_url?: string; accepted_at?: string }) => ({
    id: a.id,
    name: a.name,
    slug: a.slug,
    logoUrl: a.logo_url || undefined,
    role: a.role,
    address: a.address || undefined,
    city: a.city || undefined,
    state: a.state || undefined,
    phone: a.phone || undefined,
    websiteUrl: a.website_url || undefined,
    acceptedAt: a.accepted_at || undefined,
  }));

  return {
    id: userData.id.toString(),
    name: userData.name || 'Lutador Desconhecido',
    nickname: userData.nickname || '',
    startDate: bjjProfile?.started_training_at || '',
    birthday: userData.birthday || undefined,
    photoUrl: userData.photo_url || '',
    companies,
    beltHistory: belts.map(convertBeltToPromotion),
    monthlyTrainingCount: userData.monthly_training_count || 0,
    competitionCategories: userData.competition_categories
      ? {
          ageDivision: userData.competition_categories.age_division || null,
          weightCategory: userData.competition_categories.weight_category || null,
        }
      : null,
  };
}

export default function PublicProfile() {
  const { slug } = useParams({ from: '/p/$slug' });

  const { data: rawData, isPending, isError } = useQuery({
    queryKey: ['public-profile', slug],
    queryFn: async () => {
      const response = await fetch(`${PUBLIC_PROFILES_PATH}/${slug}`);
      if (!response.ok) throw new Error('Profile not found');
      return response.json();
    },
    enabled: !!slug,
    // Refetch on mount so belt/grade edits show up right away. The server response is
    // cheap (version-keyed Rails.cache), so there's no cost in always revalidating.
    staleTime: 0,
    gcTime: 60 * 60 * 1000,
  });

  const profile = useMemo(() => {
    if (!rawData) return null;
    return mapApiToProfile(rawData);
  }, [rawData]);

  useEffect(() => {
    if (!rawData) return;
    trackEvent('profile_viewed', {
      profile_id: rawData.id.toString(),
      profile_name: rawData.name || '',
      profile_slug: slug,
      current_belt: rawData.bjj_profile?.current_belt?.rank || '',
    });
  }, [rawData, slug]);

  if (isPending) return <div className="p-10 text-center text-foreground">Carregando...</div>;
  if (isError) return <div className="p-10 text-center text-destructive">Lutador não encontrado</div>;
  if (!profile) return null;

  return <ProfileView profile={profile} slug={slug} />;
}
